import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bell, Film, Loader2, Mail, Plus, Trash2, Copy, Video, Sparkles } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileUrl } from "./HeroSlides";

const card = "bg-white border border-[var(--line)] rounded-2xl p-5 space-y-3";

export function StoriesManager() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");
  const ref = useRef();
  const load = () => api.get("/weddings/mine/stories").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  async function add(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f); fd.append("caption", caption);
    try { await api.post("/weddings/mine/stories", fd); setCaption(""); toast.success("Story pinned to the top of your feed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }
  async function remove(id) {
    try { await api.delete(`/weddings/mine/stories/${id}`); setRows((r) => r.filter((x) => x.story_id !== id)); toast.success("Story removed"); }
    catch (err) { toast.error(formatApiError(err)); }
  }
  return (
    <div className={card} data-testid="stories-manager">
      <p className="text-sm font-medium flex items-center gap-2"><Film className="w-4 h-4 text-[var(--gold)]" /> Couple stories <span className="text-xs text-[var(--muted)]">· pinned at the top of your feed until you remove them</span></p>
      <div className="flex flex-wrap gap-3">
        {rows.map((s) => (
          <div key={s.story_id} className="relative w-20 h-32 rounded-xl overflow-hidden bg-black" data-testid={`story-item-${s.story_id}`}>
            {s.type === "video" ? <video src={fileUrl(s.path)} muted className="w-full h-full object-cover" /> : <img src={fileUrl(s.path)} alt="" className="w-full h-full object-cover" />}
            <button onClick={() => remove(s.story_id)} className="absolute top-1 right-1 bg-black/60 rounded-full p-1 text-white" data-testid={`story-remove-${s.story_id}`} aria-label="Remove story"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <button onClick={() => ref.current?.click()} disabled={busy || rows.length >= 10} className="w-20 h-32 rounded-xl border-2 border-dashed border-[var(--line)] flex flex-col items-center justify-center text-xs text-[var(--muted)] hover:border-[var(--gold)] transition-colors disabled:opacity-40" data-testid="story-add-btn">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Plus className="w-5 h-5" /> Add</>}
        </button>
      </div>
      <input value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={120} placeholder="Caption for the next story (optional)" className="input-luxe text-sm" data-testid="story-caption-input" />
      <input ref={ref} type="file" accept="video/*,image/*" className="hidden" onChange={add} data-testid="story-file-input" />
    </div>
  );
}

const ALERTS = [["on_post", "Guests post a photo, video or replay"], ["on_comment", "Someone comments"], ["on_live", "A guest goes live"]];

export function FeedAlertsCard() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/weddings/mine/feed-alerts").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return null;
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  async function save() {
    setSaving(true);
    try { const { recent, ...body } = p; await api.put("/weddings/mine/feed-alerts", body); toast.success("Feed alerts saved"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }
  const box = (k, label) => (
    <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!p[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`alerts-${k}`} /> {label}</label>
  );
  return (
    <div className={card} data-testid="feed-alerts-card">
      <p className="text-sm font-medium flex items-center gap-2"><Bell className="w-4 h-4 text-[var(--gold)]" /> Feed alerts</p>
      <p className="text-xs text-[var(--muted)]">Tell me when…</p>
      <div className="space-y-1.5">{ALERTS.map(([k, l]) => box(k, l))}</div>
      <p className="text-xs text-[var(--muted)] pt-2">How should we tell you?</p>
      <div className="flex flex-wrap gap-4">{box("email", "Email")}{box("sms", "Text message")}</div>
      {p.sms && <input value={p.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="Mobile number, e.g. +1 404 555 0100" className="input-luxe text-sm" data-testid="alerts-phone" />}
      <button onClick={save} disabled={saving} className="btn-gold !py-2 text-sm" data-testid="alerts-save">{saving ? "Saving…" : "Save alerts"}</button>
      {p.recent?.length > 0 && <div className="pt-2 border-t border-[var(--line)] text-xs text-[var(--muted)] space-y-1" data-testid="alerts-recent">{p.recent.map((r) => <p key={r.at}>{new Date(r.at).toLocaleString()} · {r.line}</p>)}</div>}
    </div>
  );
}

export function VideoNotesCard() {
  const [d, setD] = useState({ requests: [], notes: [] });
  const [rows, setRows] = useState([{ name: "", email: "" }]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/weddings/mine/video-notes").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const edit = (i, k, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  async function send(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/weddings/mine/video-notes/requests", { recipients: rows.filter((r) => r.name && r.email), message });
      const mocked = data.some((x) => x.email_status === "mocked");
      toast.success(mocked ? "Requests saved. Email isn't switched on yet, so copy each link and send it yourself." : `Sent ${data.length} request${data.length > 1 ? "s" : ""}`);
      setRows([{ name: "", email: "" }]); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }
  async function remove(id) {
    try { await api.delete(`/weddings/mine/video-notes/${id}`); load(); toast.success("Removed from your wedding party page"); }
    catch (err) { toast.error(formatApiError(err)); }
  }
  const link = (t) => `${window.location.origin}/video-note/${t}`;
  return (
    <div className={`${card} mt-10`} data-testid="video-notes-card">
      <p className="font-serif text-2xl flex items-center gap-2"><Video className="w-5 h-5 text-[var(--gold)]" /> Video notes</p>
      <p className="text-sm text-[var(--muted)]">Email family and friends a link to record a short video. Their video posts straight to your wedding party page.</p>
      <form onSubmit={send} className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid sm:grid-cols-2 gap-2">
            <input required value={r.name} onChange={(e) => edit(i, "name", e.target.value)} placeholder="Name" maxLength={60} className="input-luxe text-sm" data-testid={`vn-name-${i}`} />
            <input required type="email" value={r.email} onChange={(e) => edit(i, "email", e.target.value)} placeholder="Email" className="input-luxe text-sm" data-testid={`vn-email-${i}`} />
          </div>
        ))}
        <button type="button" onClick={() => setRows((r) => [...r, { name: "", email: "" }])} disabled={rows.length >= 25} className="text-xs text-[var(--gold)] flex items-center gap-1" data-testid="vn-add-row"><Plus className="w-3 h-3" /> Add another person</button>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={600} rows={2} placeholder="Your message, e.g. We'd love 30 seconds of your best marriage advice!" className="input-luxe text-sm" data-testid="vn-message" />
        <button disabled={busy} className="btn-gold !py-2 text-sm flex items-center gap-2" data-testid="vn-send"><Mail className="w-4 h-4" /> {busy ? "Sending…" : "Send requests"}</button>
      </form>
      {d.notes.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3" data-testid="vn-notes">
          {d.notes.map((n) => (
            <div key={n.note_id} className="relative rounded-xl overflow-hidden bg-black" data-testid={`vn-note-${n.note_id}`}>
              <video src={fileUrl(n.path)} controls playsInline className="w-full aspect-[9/16] object-cover" />
              <p className="absolute top-0 inset-x-0 p-2 text-xs text-white bg-gradient-to-b from-black/70 to-transparent">{n.name}</p>
              <button onClick={() => remove(n.note_id)} className="absolute top-1 right-1 bg-black/60 rounded-full p-1 text-white" data-testid={`vn-remove-${n.note_id}`} aria-label="Remove video note"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      {d.requests.length > 0 && (
        <div className="pt-3 border-t border-[var(--line)] space-y-1.5" data-testid="vn-requests">
          {d.requests.map((r) => (
            <div key={r.request_id} className="flex items-center gap-3 text-sm">
              <span className="flex-1 truncate">{r.name} <span className="text-[var(--muted)] text-xs">{r.email}</span></span>
              <span className="text-[10px] uppercase tracking-wider text-[var(--gold)]">{r.status === "done" ? "Received" : r.status === "removed" ? "Removed" : r.email_status === "mocked" ? "Share link" : "Waiting"}</span>
              {r.status === "sent" && <button onClick={() => { navigator.clipboard?.writeText(link(r.token)); toast.success("Link copied"); }} className="text-[var(--muted)] hover:text-[var(--ink)]" data-testid={`vn-copy-${r.request_id}`} aria-label="Copy link"><Copy className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function BrandMarkCard() {
  const [b, setB] = useState(null);
  const [text, setText] = useState("");
  const [style, setStyle] = useState("");
  const [busy, setBusy] = useState("");
  const ref = useRef();
  useEffect(() => { api.get("/weddings/mine/brand-mark").then((r) => { setB(r.data); setText(r.data.text || ""); }).catch(() => {}); }, []);
  async function run(kind, fn) {
    setBusy(kind);
    try { const { data } = await fn(); setB(data); toast.success(data.type === "none" ? "Mark removed" : "Your mark is on every shared photo and clip"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(""); }
  }
  const upload = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (!f) return; const fd = new FormData(); fd.append("file", f); run("upload", () => api.post("/weddings/mine/brand-mark/upload", fd)); };
  if (!b) return null;
  return (
    <div className={card} data-testid="brand-mark-card">
      <p className="text-sm font-medium flex items-center gap-2"><Sparkles className="w-4 h-4 text-[var(--gold)]" /> Your logo or mark <span className="text-xs text-[var(--muted)]">· sits along the bottom of shared photos and highlight clips</span></p>
      <div className="rounded-xl bg-gradient-to-b from-[#333] to-black h-24 flex flex-col items-center justify-center gap-1" data-testid="brand-mark-preview">
        {b.type === "image" ? <img src={fileUrl(b.path)} alt="Your logo" className="max-h-12 max-w-[60%] object-contain" /> : b.type === "text" ? <p className="text-[var(--gold)] font-semibold">{b.text}</p> : <p className="text-white/40 text-xs">No mark yet</p>}
        <p className="text-[10px] text-white/70">lockedinforever.com</p>
      </div>
      <div className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={40} placeholder="Text mark, e.g. A & L · 06.14.26" className="input-luxe text-sm flex-1" data-testid="brand-text-input" />
        <button onClick={() => run("text", () => api.put("/weddings/mine/brand-mark", { type: "text", text }))} disabled={!!busy || !text.trim()} className="btn-outline-gold !py-2 text-sm" data-testid="brand-text-save">Use text</button>
      </div>
      <div className="flex gap-2">
        <input value={style} onChange={(e) => setStyle(e.target.value)} maxLength={160} placeholder="Logo style (optional), e.g. rose gold script with a heart" className="input-luxe text-sm flex-1" data-testid="brand-style-input" />
        <button onClick={() => run("ai", () => api.post("/weddings/mine/brand-mark/generate", { style }))} disabled={!!busy} className="btn-gold !py-2 text-sm flex items-center gap-1" data-testid="brand-generate">{busy === "ai" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Make logo</button>
      </div>
      <div className="flex gap-4 text-xs">
        <button onClick={() => ref.current?.click()} disabled={!!busy} className="text-[var(--gold)]" data-testid="brand-upload-btn">{busy === "upload" ? "Uploading…" : "Upload your own logo"}</button>
        {b.type !== "none" && <button onClick={() => run("none", () => api.put("/weddings/mine/brand-mark", { type: "none", text: "" }))} className="text-[var(--muted)]" data-testid="brand-remove">Remove mark</button>}
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="brand-file-input" />
    </div>
  );
}
